import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import MainHeader from '../../components/MainHeader';
import Footer from '../../components/Footer';
import './CoupleLinkScreen.css';

export default function CoupleLinkScreen() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [partnerEmail, setPartnerEmail] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  async function handleLinkPartner(e) {
    e.preventDefault();
    if (!partnerEmail) {
      setError('Please enter your partner\'s email');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data } = await api.post('/auth/couple/link', {
        partnerEmail,
        partnerName: partnerName || undefined
      });

      setSuccess(data.message || 'Partner linked successfully!');
      await refreshUser();
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to link partner');
    } finally {
      setLoading(false);
    }
  }

  async function handleUnlinkPartner() {
    if (!window.confirm('Are you sure you want to unlink your partner? This will affect couple mode sessions.')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data } = await api.post('/auth/couple/unlink');
      setSuccess(data.message || 'Partner unlinked successfully');
      await refreshUser();
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to unlink partner');
    } finally {
      setLoading(false);
    }
  }

  const hasPartner = user?.partner && user?.coupleStatus === 'active';

  return (
    <div className="main-screen">
      <MainHeader title="Partner Link" showBack={true} backPath="/home" />

      <main className="screen-main">
        <div className="couple-link-container">
          {hasPartner ? (
            <div className="partner-linked">
              <div className="linked-icon">💑</div>
              <h2 className="linked-title">Partner Linked!</h2>
              <div className="partner-info-card">
                <h3 className="partner-name">{user.partnerName}</h3>
                <p className="partner-status">Couple mode is active</p>
              </div>

              <div className="linked-benefits">
                <h4 className="benefits-title">What you can do together:</h4>
                <ul className="benefits-list">
                  <li>🍽️ Start couple sessions instantly</li>
                  <li>📊 Share decision history</li>
                  <li>🔔 Send hunger nudges</li>
                  <li>🎯 Find restaurants you both love</li>
                </ul>
              </div>

              <button 
                className="btn btn-danger btn-lg"
                onClick={handleUnlinkPartner}
                disabled={loading}
              >
                {loading ? 'Unlinking...' : 'Unlink Partner'}
              </button>
            </div>
          ) : (
            <div className="partner-link-form">
              <div className="form-icon">💕</div>
              <h2 className="form-title">Link Your Partner</h2>
              <p className="form-description">
                Connect with your partner to use couple mode and make food decisions together!
              </p>

              {error && <div className="auth-error">{error}</div>}
              {success && <div className="auth-success">{success}</div>}

              <form onSubmit={handleLinkPartner}>
                <div className="form-group">
                  <label htmlFor="partnerEmail">Partner's Email *</label>
                  <input
                    id="partnerEmail"
                    type="email"
                    value={partnerEmail}
                    onChange={(e) => setPartnerEmail(e.target.value)}
                    placeholder="partner@email.com"
                    disabled={loading}
                    required
                  />
                  <p className="input-hint">They must have a Hangrily account</p>
                </div>

                <div className="form-group">
                  <label htmlFor="partnerName">Partner's Name (Optional)</label>
                  <input
                    id="partnerName"
                    type="text"
                    value={partnerName}
                    onChange={(e) => setPartnerName(e.target.value)}
                    placeholder="e.g., Alex"
                    disabled={loading}
                  />
                </div>

                <button 
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={loading}
                >
                  {loading ? 'Linking...' : 'Link Partner'}
                </button>
              </form>

              <div className="info-box">
                <h4 className="info-title">How it works</h4>
                <ol className="info-list">
                  <li>Enter your partner's email address</li>
                  <li>They'll be instantly linked to your account</li>
                  <li>Start using couple mode together!</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
